import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import SidebarBreakdownCard from './SidebarBreakdownCard';
import SidebarEntityTilesCard from './SidebarEntityTilesCard';
import SidebarGroupBarsCard from './SidebarGroupBarsCard';
import SidebarSelectedCard from './SidebarSelectedCard';
import SidebarStorageCard from './SidebarStorageCard';
import {
  useExplorationActions,
  useExplorationData,
  useExplorationState,
  useFilteredVolumes,
} from '../../context/ExplorationContext';
import { isDrillLocationEmpty } from '../../lib/drill/drillLocation';
import { shouldAutoCollapseSidebar } from '../../lib/explorationUrlVolumeHydration';
import { EXPLORATION_VOLUME_PARAM } from '../../lib/explorationUrlParams';
import { isCategoryLeafSidebarScope } from '../../lib/sidebar/sidebarPalettes';
import {
  explorationSidebarEmptyFiltersMessage,
  explorationSidebarEmptyScopeMessage,
} from '../explorationCanvasChrome';
import { isExplorationScopeEmptyFromCounts } from '../../lib/isExplorationCanvasScopeEmpty';
import { volumesInCurrentScope } from '../../lib/sidebar/volumesInCurrentScope';
import Typography from '~/shared/components/Typography';

export default function ExplorationSidebar() {
  const state = useExplorationState();
  const actions = useExplorationActions();
  const { accounts, initialLoading } = useExplorationData();
  const [searchParams] = useSearchParams();
  const filtered = useFilteredVolumes();
  const location = state.drillPath;

  const detailOnly =
    state.selectedNode?.kind === 'volume' || state.selectedNode?.kind === 'instance';

  const scopedVolumes = useMemo(() => {
    if (detailOnly) return [];
    return volumesInCurrentScope(filtered, {
      secondary: state.secondary,
      location,
      groupByAccount: state.groupByAccount,
      tagKey: state.tagKey,
      view: state.view,
    });
  }, [
    filtered,
    state.secondary,
    location,
    state.groupByAccount,
    state.tagKey,
    state.view,
    detailOnly,
  ]);

  const scoped = state.sidebarCollapsed ? [] : scopedVolumes;
  const isActivation = state.view === 'activation';
  const hasScopedData = scopedVolumes.length > 0;
  const hasActiveConstraints = state.filters.length > 0 || state.searchQuery.trim().length > 0;
  const showFiltersEmpty = !detailOnly && filtered.length === 0 && hasActiveConstraints;
  const drillIsActive = !isDrillLocationEmpty(location);
  const showScopeEmpty =
    !detailOnly &&
    isExplorationScopeEmptyFromCounts(filtered.length, drillIsActive ? 1 : 0, scopedVolumes.length);
  const atCategoryLeaf = useMemo(() => isCategoryLeafSidebarScope(location), [location]);
  const urlVolumeId = searchParams.get(EXPLORATION_VOLUME_PARAM);
  const selectedVolumeId =
    state.selectedNode?.kind === 'volume' ? state.selectedNode.volume.ebsId : null;

  useEffect(() => {
    if (
      !shouldAutoCollapseSidebar({
        initialLoading,
        accountId: state.accountId,
        hasScopedData,
        hasSelection: state.selectedNode != null,
        sidebarCollapsed: state.sidebarCollapsed,
        urlVolumeId,
        selectedVolumeId,
        accounts,
        drillPathLength: drillIsActive ? 1 : 0,
      })
    ) {
      return;
    }

    actions.setSidebarCollapsed(true);
  }, [
    accounts,
    actions,
    hasScopedData,
    initialLoading,
    selectedVolumeId,
    state.accountId,
    state.selectedNode,
    state.sidebarCollapsed,
    drillIsActive,
    urlVolumeId,
  ]);

  if (state.sidebarCollapsed) return null;

  return (
    <>
      {state.selectedNode ? <SidebarSelectedCard selection={state.selectedNode} /> : null}

      {showFiltersEmpty ? (
        <Typography
          variant="p3-reg"
          color="text-gray-700"
          className="px-6 py-8 text-center"
          role="status"
        >
          {explorationSidebarEmptyFiltersMessage}
        </Typography>
      ) : null}

      {showScopeEmpty ? (
        <Typography
          variant="p3-reg"
          color="text-gray-700"
          className="px-6 py-8 text-center"
          role="status"
        >
          {explorationSidebarEmptyScopeMessage}
        </Typography>
      ) : null}

      {!detailOnly && hasScopedData && (
        <>
          <SidebarStorageCard scoped={scoped} singleMetric={atCategoryLeaf} />

          {!atCategoryLeaf &&
            (isActivation ? (
              <SidebarBreakdownCard variant="activation" volumes={scoped} />
            ) : (
              <SidebarBreakdownCard variant="support" volumes={scoped} />
            ))}

          <SidebarEntityTilesCard scoped={scoped} />

          <SidebarGroupBarsCard volumes={filtered} scopedVolumes={scoped} />
        </>
      )}
    </>
  );
}
